import { useCallback, useEffect, useRef, useState } from 'react'
import apiClient from '../api/client'

const DISMISSED_UNTIL_KEY = 'cardsmith_notif_dismissed_until'

// A press must not be able to hang. `navigator.serviceWorker.ready` neither
// resolves nor rejects when no worker is registered in scope — it simply waits
// forever — so every await of it is bounded here. Ten seconds is long enough
// for a slow first install on a cold cache and short enough that a person is
// told something rather than left watching a control that never answers.
const SW_READY_TIMEOUT_MS = 10_000

/** Convert VAPID base64url public key to Uint8Array for PushManager.subscribe() */
// Return an ArrayBuffer-backed Uint8Array (not ArrayBufferLike) so it satisfies
// PushSubscriptionOptionsInit.applicationServerKey (BufferSource) under lib.dom's
// SharedArrayBuffer-aware typings.
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)))
}

export type PushPermission = 'unsupported' | 'default' | 'granted' | 'denied'

/**
 * What the notifications control is currently true of. Every value is a state
 * the person can be shown distinctly — there is deliberately no single "off"
 * that quietly covers a refusal, an unsupported browser, or an account that
 * disagrees with this device.
 */
export type NotificationState =
  | 'checking'             // still reading permission / subscription / account
  | 'unsupported'          // this browser cannot do push at all
  | 'blocked'              // permission denied — recoverable only in browser/device settings
  | 'on'                   // account on and this device subscribed
  | 'off'                  // account off and this device not subscribed
  | 'accountOnDeviceOff'   // account on, other device(s) subscribed, this one is not
  | 'accountOnNoDevice'    // account on but nothing is subscribed anywhere — nothing arrives
  | 'deviceOnAccountOff'   // this browser holds a subscription the account has no record of

/** Why the last attempt stopped, or what could not be read. */
export type NotificationIssue =
  | 'dismissed'          // permission prompt closed without an answer — ask again
  | 'refused'            // permission just declined — fixable in site settings
  | 'blocked'            // permission was already denied before we asked
  | 'workerUnavailable'  // service worker never became ready within the bound
  | 'keyUnavailable'     // VAPID public key could not be fetched
  | 'subscribeFailed'    // the browser refused to create a subscription
  | 'registerFailed'     // browser subscribed, but the account did not record it
  | 'disableFailed'      // turning off did not complete
  | 'statusUnavailable'  // account state could not be read; browser state only

interface PushStatus {
  notifications_enabled: boolean
  has_active_subscription: boolean
}

class PushError extends Error {
  // Assigned in the body rather than declared as a parameter property —
  // `erasableSyntaxOnly` is on, and a parameter property is not erasable.
  readonly issue: NotificationIssue

  constructor(issue: NotificationIssue) {
    super(issue)
    this.name = 'PushError'
    this.issue = issue
  }
}

/** Reject with `issue` if `promise` has not settled within `ms`. */
function withTimeout<T>(promise: Promise<T>, ms: number, issue: NotificationIssue): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new PushError(issue)), ms)
    promise.then(
      (value) => { clearTimeout(timer); resolve(value) },
      (err) => { clearTimeout(timer); reject(err) },
    )
  })
}

/**
 * Ask for notification permission explicitly.
 *
 * Not a side effect of `pushManager.subscribe()`: Chromium prompts from
 * `subscribe()`, WebKit never does — it rejects with NotAllowedError and shows
 * the person nothing, so a control that relies on the implicit prompt can never
 * be switched on at all on iOS/iPadOS however many times it is pressed.
 *
 * Handles both shapes of the API: the modern promise and the legacy callback
 * (Safari before 16.4), without calling it twice.
 */
function requestPermission(): Promise<NotificationPermission> {
  return new Promise((resolve) => {
    let settled = false
    const done = (result: NotificationPermission) => {
      if (settled) return
      settled = true
      resolve(result)
    }
    try {
      const maybePromise = Notification.requestPermission(done) as
        | Promise<NotificationPermission>
        | undefined
      if (maybePromise && typeof maybePromise.then === 'function') {
        maybePromise.then(done, () => done(Notification.permission))
      }
    } catch {
      done(Notification.permission)
    }
  })
}

/** True when an existing subscription was created with the key we are about to use. */
function matchesKey(existing: ArrayBuffer | null | undefined, key: Uint8Array): boolean {
  if (!existing) return false
  const a = new Uint8Array(existing)
  if (a.length !== key.length) return false
  return a.every((byte, i) => byte === key[i])
}

interface DeriveInput {
  isSupported: boolean
  loaded: boolean
  permission: PushPermission
  hasLocalSubscription: boolean
  server: PushStatus | null
}

/**
 * The single place the control's state is decided. Exported for its own test —
 * this is the rule that used to be `permission === 'granted' && isSubscribed`,
 * which read the browser while the press wrote to the account, so a state the
 * server held could never appear on screen.
 */
export function deriveNotificationState({
  isSupported,
  loaded,
  permission,
  hasLocalSubscription,
  server,
}: DeriveInput): NotificationState {
  if (!isSupported) return 'unsupported'
  if (!loaded) return 'checking'
  if (permission === 'denied') return 'blocked'

  const localOn = hasLocalSubscription && permission === 'granted'

  // Account state unreadable (or not requested): fall back to what this
  // browser can prove, which the caller says out loud rather than passing off
  // as the whole truth.
  if (!server) return localOn ? 'on' : 'off'

  if (server.notifications_enabled) {
    if (localOn) return 'on'
    return server.has_active_subscription ? 'accountOnDeviceOff' : 'accountOnNoDevice'
  }
  return localOn ? 'deviceOnAccountOff' : 'off'
}

interface Options {
  /**
   * Read the account's own notification state via `GET /push/status` as well as
   * the browser's. Opt-in: the settings surface needs it, the in-app prompt
   * (mounted on every authenticated page) does not, and it should not add a
   * request to every page load to find that out.
   */
  withServerStatus?: boolean
}

export function usePushNotifications({ withServerStatus = false }: Options = {}) {
  const [permission, setPermission] = useState<PushPermission>('default')
  const [hasLocalSubscription, setHasLocalSubscription] = useState(false)
  const [server, setServer] = useState<PushStatus | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [issue, setIssue] = useState<NotificationIssue | null>(null)
  const mounted = useRef(true)

  const isSupported =
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window

  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])

  /** Wait for the active registration, bounded — never indefinitely. */
  const readyRegistration = useCallback(async (): Promise<ServiceWorkerRegistration> => {
    return withTimeout(navigator.serviceWorker.ready, SW_READY_TIMEOUT_MS, 'workerUnavailable')
  }, [])

  const readLocalSubscription = useCallback(async (): Promise<PushSubscription | null> => {
    const reg = await readyRegistration()
    return reg.pushManager.getSubscription()
  }, [readyRegistration])

  /** Re-read the account's own state. Null on failure, which is reported. */
  const loadServerStatus = useCallback(async (): Promise<void> => {
    if (!withServerStatus) return
    try {
      const { data } = await apiClient.get<PushStatus>('/api/v1/push/status')
      if (!mounted.current) return
      setServer(data)
      setIssue((prev) => (prev === 'statusUnavailable' ? null : prev))
    } catch {
      if (!mounted.current) return
      setServer(null)
      setIssue('statusUnavailable')
    }
  }, [withServerStatus])

  /** Re-read both sides — this browser and, when asked for, the account. */
  const refresh = useCallback(async (): Promise<void> => {
    if (!isSupported) {
      setLoaded(true)
      return
    }
    setPermission(Notification.permission as PushPermission)

    let local = false
    try {
      local = !!(await readLocalSubscription())
    } catch {
      // A worker that never becomes ready cannot be holding a subscription we
      // could act on, so this device counts as not subscribed. It is not
      // reported as a failure here: nothing was attempted yet.
      local = false
    }
    if (!mounted.current) return
    setHasLocalSubscription(local)

    await loadServerStatus()
    if (!mounted.current) return
    setLoaded(true)
  }, [isSupported, loadServerStatus, readLocalSubscription])

  useEffect(() => {
    void refresh()
  }, [refresh])

  /**
   * Ask for permission, subscribe, and register the subscription with the
   * account. Returns true only when the account has recorded it.
   */
  const enable = useCallback(async (): Promise<boolean> => {
    if (!isSupported) return false
    setIssue(null)
    setIsLoading(true)
    try {
      // 1. Permission, explicitly, before anything is awaited — the click's
      //    user activation is still fresh here, and a refusal, a dismissal and
      //    a standing block are three different answers.
      if (Notification.permission !== 'granted') {
        if (Notification.permission === 'denied') {
          setPermission('denied')
          setIssue('blocked')
          return false
        }
        const result = await requestPermission()
        if (!mounted.current) return false
        setPermission(result as PushPermission)
        if (result === 'denied') { setIssue('refused'); return false }
        if (result !== 'granted') { setIssue('dismissed'); return false }
      }

      // 2. The application server key.
      let key: Uint8Array<ArrayBuffer>
      try {
        const { data } = await apiClient.get<{ public_key: string }>(
          '/api/v1/push/vapid-public-key',
        )
        if (!data || typeof data.public_key !== 'string' || !data.public_key) {
          throw new PushError('keyUnavailable')
        }
        key = urlBase64ToUint8Array(data.public_key)
      } catch {
        throw new PushError('keyUnavailable')
      }

      // 3. The worker, bounded.
      const reg = await readyRegistration()

      // 4. Reuse the browser's existing subscription when it was made with this
      //    key; replace it when it was not, since subscribing over a mismatched
      //    key throws and the old endpoint is one nothing can send to.
      let sub = await reg.pushManager.getSubscription()
      if (sub && !matchesKey(sub.options.applicationServerKey, key)) {
        await sub.unsubscribe().catch(() => {})
        sub = null
      }
      if (!sub) {
        try {
          sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: key,
          })
        } catch {
          throw new PushError('subscribeFailed')
        }
      }

      // 5. Register it with the account. If this fails, the browser must not be
      //    left holding a subscription the account has no record of: the next
      //    press would reuse that same endpoint and fail identically forever.
      //    Dropping it means pressing again starts clean and can succeed.
      const payload = sub.toJSON() as {
        endpoint: string
        keys: { p256dh: string; auth: string }
      }
      try {
        await apiClient.post('/api/v1/push/subscribe', {
          endpoint: payload.endpoint,
          keys: payload.keys,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          user_agent: navigator.userAgent.slice(0, 256),
        })
      } catch {
        await sub.unsubscribe().catch(() => {})
        throw new PushError('registerFailed')
      }

      if (!mounted.current) return true
      setHasLocalSubscription(true)
      setPermission('granted')
      // Both sides are now known first-hand; no extra round trip to learn it.
      if (withServerStatus) {
        setServer({ notifications_enabled: true, has_active_subscription: true })
      }
      setLoaded(true)
      return true
    } catch (err) {
      if (mounted.current) {
        setIssue(err instanceof PushError ? err.issue : 'subscribeFailed')
        if ('Notification' in window) {
          setPermission(Notification.permission as PushPermission)
        }
      }
      return false
    } finally {
      if (mounted.current) setIsLoading(false)
    }
  }, [isSupported, readyRegistration, withServerStatus])

  /**
   * Turn reminders off for the account and drop this browser's subscription.
   * The account flag is what the reminder job reads, so this stops reminders on
   * every device, not only this one.
   */
  const disable = useCallback(async (): Promise<void> => {
    if (!isSupported) return
    setIssue(null)
    setIsLoading(true)
    try {
      // A missing worker must not prevent turning reminders off — the account
      // flag is the part that matters, and it is reachable either way.
      const sub = await readLocalSubscription().catch(() => null)
      await apiClient.delete('/api/v1/push/unsubscribe', {
        data: sub ? { endpoint: sub.endpoint } : {},
      })
      if (sub) await sub.unsubscribe().catch(() => {})
      if (!mounted.current) return
      setHasLocalSubscription(false)
      // Deleting one endpoint clears the account flag but leaves other devices'
      // rows, so what the account holds now is read, not assumed. Only the
      // account is re-read: this browser's side was just settled first-hand,
      // and re-reading it would pay the service-worker wait for nothing.
      await loadServerStatus()
    } catch {
      if (mounted.current) setIssue('disableFailed')
    } finally {
      if (mounted.current) setIsLoading(false)
    }
  }, [isSupported, loadServerStatus, readLocalSubscription])

  /** True if we should surface the in-app prompt to the user. */
  const shouldShowPrompt = useCallback((): boolean => {
    if (!isSupported) return false
    if (Notification.permission !== 'default') return false
    const until = localStorage.getItem(DISMISSED_UNTIL_KEY)
    if (until && Date.now() < Number(until)) return false
    return true
  }, [isSupported])

  /** Snooze the in-app prompt for 7 days (without denying browser permission). */
  const dismissPrompt = useCallback((): void => {
    localStorage.setItem(
      DISMISSED_UNTIL_KEY,
      String(Date.now() + 7 * 24 * 60 * 60 * 1000)
    )
  }, [])

  const state = deriveNotificationState({
    isSupported,
    loaded,
    permission,
    hasLocalSubscription,
    server,
  })

  return {
    isSupported,
    permission,
    state,
    issue,
    /** The account's own view, when it could be read. Null when it could not. */
    serverStatus: server,
    isLoading,
    enable,
    disable,
    refresh,
    clearIssue: useCallback(() => setIssue(null), []),
    /** Alias kept for NotificationPrompt, which only needs "turn it on". */
    subscribe: enable,
    shouldShowPrompt,
    dismissPrompt,
  }
}
